import { createApi } from '@reduxjs/toolkit/query/react';
import { saveAs } from 'file-saver';
//
import { customBaseQuery } from '../../auth/context/jwt/utils';
import axios from '../../utils/axios';
import {
  IIncome,
  IIncomeCreate,
  IIncomePage,
  IIncomePaymentCreate,
  IIncomeSummary,
} from '../../types/income';

const path = 'incomes';

export const incomeService = createApi({
  reducerPath: 'incomeService',
  tagTypes: ['incomes'],
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getIncomes: builder.query<IIncomePage, Record<string, unknown>>({
      query: (params) => ({ url: `/${path}/list`, params }),
      providesTags: ['incomes'],
    }),

    getIncomeSummary: builder.query<IIncomeSummary, number | void>({
      query: (clientId) => ({ url: `/${path}/summary`, params: clientId ? { clientId } : {} }),
      providesTags: ['incomes'],
    }),

    getIncome: builder.query<IIncome, number>({
      query: (id) => ({ url: `/${path}/findById/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'incomes', id }],
    }),

    saveIncome: builder.mutation<IIncome, IIncomeCreate>({
      query: (body) => ({ url: `/${path}/insert`, method: 'POST', body }),
      invalidatesTags: ['incomes'],
    }),

    updateIncome: builder.mutation<IIncome, { id: number; data: IIncomeCreate }>({
      query: ({ id, data }) => ({ url: `/${path}/update/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['incomes'],
    }),

    deleteIncome: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/${path}/delete/${id}`, method: 'DELETE' }),
      invalidatesTags: ['incomes'],
    }),

    cancelIncome: builder.mutation<IIncome, { id: number; reason: string }>({
      query: ({ id, reason }) => ({ url: `/${path}/${id}/cancel`, method: 'POST', body: { reason } }),
      invalidatesTags: ['incomes'],
    }),

    registerPayment: builder.mutation<IIncome, { id: number; data: IIncomePaymentCreate }>({
      query: ({ id, data }) => ({ url: `/${path}/${id}/payments`, method: 'POST', body: data }),
      invalidatesTags: ['incomes'],
    }),

    deletePayment: builder.mutation<IIncome, { id: number; paymentId: number }>({
      query: ({ id, paymentId }) => ({ url: `/${path}/${id}/payments/${paymentId}`, method: 'DELETE' }),
      invalidatesTags: ['incomes'],
    }),
  }),
});

export const downloadReceiptPdf = async (id: number, paymentId: number, fallbackName: string) => {
  const response = await axios.get(`/${path}/${id}/payments/${paymentId}/receipt`, { responseType: 'blob' });
  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  const fileName = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '').split(';')[0] || fallbackName
    : fallbackName;
  saveAs(new Blob([response.data], { type: 'application/pdf' }), fileName);
};

export const {
  useGetIncomesQuery,
  useGetIncomeSummaryQuery,
  useGetIncomeQuery,
  useSaveIncomeMutation,
  useUpdateIncomeMutation,
  useDeleteIncomeMutation,
  useCancelIncomeMutation,
  useRegisterPaymentMutation,
  useDeletePaymentMutation,
} = incomeService;
